import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const [otp, setOtp]                     = useState("");
  const [newPassword, setNewPassword]     = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading]             = useState(false);
  const [success, setSuccess]             = useState(false);
  const [error, setError]                 = useState("");
  const [otpExpired, setOtpExpired]       = useState(false);
  const [resending, setResending]         = useState(false);
  const [resendMsg, setResendMsg]         = useState("");

  const handleSubmit = async () => {
    if (!otp || otp.length < 6)   { setError("Please enter the 6-digit OTP."); return; }
    if (!newPassword)              { setError("Please enter a new password."); return; }
    if (newPassword.length < 6)   { setError("Password must be at least 6 characters."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }

    setLoading(true);
    setError("");
    setOtpExpired(false);
    setResendMsg("");

    try {
      await axios.post(`${API_BASE_URL}/auth/reset-password`, { email, otp, newPassword });
      setSuccess(true);
      setTimeout(() => navigate("/"), 3000);
    } catch (e) {
      const msg = e.response?.data?.error || "Something went wrong. Please try again.";
      if (msg.toLowerCase().includes("expired")) {
        setOtpExpired(true);
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!email) return;
    setResending(true);
    setResendMsg("");
    setError("");
    setOtpExpired(false);
    try {
      await axios.post(`${API_BASE_URL}/auth/forgot-password`, { email });
      setResendMsg("A new OTP has been sent to your email.");
      setOtp("");
    } catch (e) {
      const status = e.response?.status;
      if (status === 429) {
        setError("Too many requests. Please wait a moment and try again.");
      } else {
        setError("Failed to resend OTP. Please try again.");
      }
    } finally {
      setResending(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, background: "var(--bg-primary)" }}>
      <style>{`
        @keyframes rpFadeIn {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rp-card {
          background: var(--bg-card);
          border: 1px solid var(--border-default);
          border-radius: var(--radius-md);
          padding: 40px 36px;
          width: 100%;
          max-width: 420px;
          animation: rpFadeIn 0.3s ease both;
        }
        .rp-title {
          font-family: var(--font-serif);
          font-size: 26px;
          font-weight: 400;
          color: var(--text-logo);
          margin: 0 0 6px;
        }
        .rp-sub {
          font-size: 13px;
          color: var(--text-muted);
          line-height: 1.6;
          margin: 0 0 28px;
        }
        .rp-error {
          font-size: 12px;
          color: var(--danger-text);
          padding: 10px 12px;
          background: var(--danger-bg);
          border: 1px solid var(--danger-border);
          border-radius: var(--radius-sm);
          margin-top: 12px;
        }
        .rp-success-box {
          font-size: 12px;
          color: var(--success-text, #22c55e);
          padding: 10px 12px;
          background: var(--success-bg, rgba(34,197,94,0.08));
          border: 1px solid var(--success-border, rgba(34,197,94,0.25));
          border-radius: var(--radius-sm);
          margin-top: 12px;
        }
        .rp-link {
          background: none;
          border: none;
          font-family: var(--font-sans);
          font-size: 12px;
          color: var(--accent);
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          text-underline-offset: 2px;
          transition: opacity 0.15s;
        }
        .rp-link:hover { opacity: 0.8; }
        .rp-link:disabled { opacity: 0.4; cursor: default; }
        .rp-otp-input {
          letter-spacing: 0.35em;
          font-size: 22px;
          text-align: center;
          font-family: var(--font-mono, monospace);
        }
        .rp-success-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--accent-bg, rgba(16,185,129,0.12));
          border: 1px solid var(--accent);
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          color: var(--accent);
          font-size: 22px;
        }
      `}</style>

      <div className="rp-card">
        {/* Logo */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 28 }}>
          <span className="tc-dot" />
          <span style={{ fontFamily: "var(--font-serif)", fontSize: 18, color: "var(--text-logo)", letterSpacing: "0.01em" }}>
            TableCure
          </span>
        </div>

        {success ? (
          <div style={{ textAlign: "center" }}>
            <div className="rp-success-icon">✓</div>
            <h2 className="rp-title">Password reset!</h2>
            <p className="rp-sub">
              Your password has been updated. Redirecting you to the homepage…
            </p>
            <button
              className="tc-btn tc-btn-primary"
              style={{ width: "100%", padding: "12px", fontSize: "12px", justifyContent: "center" }}
              onClick={() => navigate("/")}
            >
              Sign in now
            </button>
          </div>
        ) : (
          <>
            <h2 className="rp-title">Reset password</h2>
            <p className="rp-sub">
              Enter the OTP sent to your email and choose a new password.
            </p>

            {/* Email — read-only */}
            <div className="tc-field">
              <label className="tc-label">Email</label>
              <input
                className="tc-input"
                type="email"
                value={email}
                readOnly
                style={{ opacity: 0.6, cursor: "default" }}
              />
            </div>

            {/* OTP */}
            <div className="tc-field">
              <label className="tc-label">One-time code</label>
              <input
                className="tc-input rp-otp-input"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="––––––"
                value={otp}
                onChange={e => { setOtp(e.target.value.replace(/\D/g, "")); setError(""); setResendMsg(""); }}
                autoFocus
              />
            </div>

            {/* New password */}
            <div className="tc-field">
              <label className="tc-label">New password</label>
              <input
                className="tc-input"
                type="password"
                placeholder="Min. 6 characters"
                value={newPassword}
                onChange={e => { setNewPassword(e.target.value); setError(""); }}
              />
            </div>

            {/* Confirm password */}
            <div className="tc-field">
              <label className="tc-label">Confirm password</label>
              <input
                className="tc-input"
                type="password"
                placeholder="Repeat your password"
                value={confirmPassword}
                onChange={e => { setConfirmPassword(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && handleSubmit()}
              />
            </div>

            {error && (
              <div className="rp-error">
                {error}
                {otpExpired && (
                  <div style={{ marginTop: 8 }}>
                    <button
                      className="rp-link"
                      onClick={handleResend}
                      disabled={resending}
                      style={{ color: "var(--danger-text)", fontWeight: 500 }}
                    >
                      {resending ? "Sending…" : "Resend OTP"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {resendMsg && <div className="rp-success-box">{resendMsg}</div>}

            <button
              className="tc-btn tc-btn-primary"
              style={{ width: "100%", padding: "12px", fontSize: "12px", justifyContent: "center", marginTop: 20, marginBottom: 16 }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <span style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span className="tc-spinner" style={{ width: 14, height: 14, borderWidth: 1.5 }} />
                  Resetting…
                </span>
              ) : "Reset password"}
            </button>

            <p style={{ textAlign: "center", fontSize: 12, color: "var(--text-muted)" }}>
              <button className="rp-link" onClick={() => navigate("/forgot-password")}>
                Back to forgot password
              </button>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
